/**
 * Renders the toast queue. Mounted once, at the app root.
 * Errors are announced assertively, everything else politely.
 */
import { useEffect } from 'react'
import { AlertTriangle, Check, Info, X } from 'lucide-react'
import { useToasts, type Toast } from './toast-store.ts'
import { useI18n } from '../../lib/i18n/i18n-store.ts'
import './Toaster.css'

const ICONS = { success: Check, error: AlertTriangle, info: Info } as const
/** The «Fichero» skin's LED, in place of the icon (hidden in the flat skin). */
const LEDS = { success: 'led--on', error: 'led--red', info: 'led--amber' } as const

function ToastItem({ toast }: { toast: Toast }) {
  const dismiss = useToasts((s) => s.dismiss)
  const { t } = useI18n()
  const Icon = ICONS[toast.tone]

  useEffect(() => {
    if (toast.duration <= 0) return
    const timer = setTimeout(() => dismiss(toast.id), toast.duration)
    return () => clearTimeout(timer)
  }, [toast.id, toast.duration, dismiss])

  return (
    <div className={`toast toast--${toast.tone}`} role={toast.tone === 'error' ? 'alert' : 'status'}>
      <span className={`led ${LEDS[toast.tone]}`} aria-hidden="true" />
      <Icon size={15} className="toast-icon" aria-hidden="true" />
      <p className="toast-message">{toast.message}</p>
      {toast.action && (
        <button
          className="btn btn--ghost toast-action"
          onClick={() => {
            toast.action!.run()
            dismiss(toast.id)
          }}
        >
          {toast.action.label}
        </button>
      )}
      <button className="btn btn--ghost btn--icon toast-close" onClick={() => dismiss(toast.id)} aria-label={t('common.dismiss')}>
        <X size={14} aria-hidden="true" />
      </button>
    </div>
  )
}

export function Toaster() {
  const toasts = useToasts((s) => s.toasts)
  return (
    <div className="toaster" aria-live="polite">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </div>
  )
}
